/**
 * CaseContextForm - Insurance/Warranty context form section
 * Feature 009: Workshop Booking & Job-Tracking System
 *
 * T056: Add CaseContext form section for insurance/warranty fields
 */

import { Building2, FileText, User, Phone, Mail, Shield, Calendar } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * Warranty types
 */
export const WARRANTY_TYPES = [
  { value: 'manufacturer', label: 'Fabricante' },
  { value: 'extended', label: 'Extendida' },
  { value: 'dealer', label: 'Concesionario' },
] as const;

/**
 * Coverage types for insurance
 */
export const COVERAGE_TYPES = [
  { value: 'full', label: 'Todo Riesgo' },
  { value: 'third_party', label: 'Terceros' },
  { value: 'third_party_extended', label: 'Terceros Ampliado' },
] as const;

/**
 * Case context data structure
 */
export interface CaseContextData {
  // Insurance fields
  insurerCompany?: string;
  claimNumber?: string;
  coverageType?: string;
  adjusterName?: string;
  adjusterPhone?: string;
  adjusterEmail?: string;
  // Warranty fields
  warrantyType?: 'manufacturer' | 'extended' | 'dealer';
  warrantyProvider?: string;
  warrantyReference?: string;
  warrantyStartDate?: number;
  preAuthRequired?: boolean;
}

interface CaseContextFormProps {
  bookingType: 'private' | 'insurance' | 'warranty';
  value: CaseContextData;
  onChange: (value: CaseContextData) => void;
  errors?: Record<string, string>;
  disabled?: boolean;
}

export function CaseContextForm({
  bookingType,
  value,
  onChange,
  errors = {},
  disabled = false,
}: CaseContextFormProps) {
  const handleChange = (field: keyof CaseContextData, fieldValue: unknown) => {
    onChange({ ...value, [field]: fieldValue });
  };

  // Don't render anything for private bookings
  if (bookingType === 'private') {
    return null;
  }

  return (
    <div className="space-y-4">
      {/* Insurance fields */}
      {bookingType === 'insurance' && (
        <>
          <div className="flex items-center gap-2 text-gray-700 mb-3">
            <Building2 className="w-5 h-5" />
            <h3 className="font-medium">Datos del Seguro</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Insurer Company */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Compañía Aseguradora *
              </label>
              <input
                type="text"
                value={value.insurerCompany || ''}
                onChange={(e) => handleChange('insurerCompany', e.target.value)}
                disabled={disabled}
                placeholder="Ej: Mapfre, Allianz, AXA..."
                className={cn(
                  'w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500',
                  errors.insurerCompany && 'border-red-500',
                  disabled && 'bg-gray-100 cursor-not-allowed'
                )}
              />
              {errors.insurerCompany && (
                <p className="mt-1 text-xs text-red-500">{errors.insurerCompany}</p>
              )}
            </div>

            {/* Claim Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Número de Siniestro
              </label>
              <div className="relative">
                <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={value.claimNumber || ''}
                  onChange={(e) => handleChange('claimNumber', e.target.value)}
                  disabled={disabled}
                  placeholder="Número de expediente"
                  className={cn(
                    'w-full pl-10 pr-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500',
                    disabled && 'bg-gray-100 cursor-not-allowed'
                  )}
                />
              </div>
            </div>

            {/* Coverage Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo de Cobertura
              </label>
              <select
                value={value.coverageType || ''}
                onChange={(e) => handleChange('coverageType', e.target.value)}
                disabled={disabled}
                className={cn(
                  'w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500',
                  disabled && 'bg-gray-100 cursor-not-allowed'
                )}
              >
                <option value="">Seleccionar cobertura</option>
                {COVERAGE_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Adjuster Info */}
          <div className="mt-4 pt-4 border-t">
            <h4 className="text-sm font-medium text-gray-600 mb-3">
              Datos del Perito (opcional)
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Adjuster Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre del Perito
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={value.adjusterName || ''}
                    onChange={(e) => handleChange('adjusterName', e.target.value)}
                    disabled={disabled}
                    placeholder="Nombre completo"
                    className={cn(
                      'w-full pl-10 pr-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500',
                      disabled && 'bg-gray-100 cursor-not-allowed'
                    )}
                  />
                </div>
              </div>

              {/* Adjuster Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Teléfono del Perito
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="tel"
                    value={value.adjusterPhone || ''}
                    onChange={(e) => handleChange('adjusterPhone', e.target.value)}
                    disabled={disabled}
                    placeholder="+34 612 345 678"
                    className={cn(
                      'w-full pl-10 pr-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500',
                      disabled && 'bg-gray-100 cursor-not-allowed'
                    )}
                  />
                </div>
              </div>

              {/* Adjuster Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email del Perito
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="email"
                    value={value.adjusterEmail || ''}
                    onChange={(e) => handleChange('adjusterEmail', e.target.value)}
                    disabled={disabled}
                    placeholder="perito@seguro.com"
                    className={cn(
                      'w-full pl-10 pr-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500',
                      disabled && 'bg-gray-100 cursor-not-allowed'
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Warranty fields */}
      {bookingType === 'warranty' && (
        <>
          <div className="flex items-center gap-2 text-gray-700 mb-3">
            <Shield className="w-5 h-5" />
            <h3 className="font-medium">Datos de Garantía</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Warranty Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo de Garantía *
              </label>
              <select
                value={value.warrantyType || ''}
                onChange={(e) =>
                  handleChange(
                    'warrantyType',
                    e.target.value as 'manufacturer' | 'extended' | 'dealer' | undefined
                  )
                }
                disabled={disabled}
                className={cn(
                  'w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500',
                  errors.warrantyType && 'border-red-500',
                  disabled && 'bg-gray-100 cursor-not-allowed'
                )}
              >
                <option value="">Seleccionar tipo</option>
                {WARRANTY_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
              {errors.warrantyType && (
                <p className="mt-1 text-xs text-red-500">{errors.warrantyType}</p>
              )}
            </div>

            {/* Warranty Provider */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Proveedor de Garantía
              </label>
              <input
                type="text"
                value={value.warrantyProvider || ''}
                onChange={(e) => handleChange('warrantyProvider', e.target.value)}
                disabled={disabled}
                placeholder="Ej: Seat, Volkswagen, Dealer..."
                className={cn(
                  'w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500',
                  disabled && 'bg-gray-100 cursor-not-allowed'
                )}
              />
            </div>

            {/* Warranty Reference */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Número de Referencia
              </label>
              <div className="relative">
                <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={value.warrantyReference || ''}
                  onChange={(e) => handleChange('warrantyReference', e.target.value)}
                  disabled={disabled}
                  placeholder="Referencia de garantía"
                  className={cn(
                    'w-full pl-10 pr-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500',
                    disabled && 'bg-gray-100 cursor-not-allowed'
                  )}
                />
              </div>
            </div>

            {/* Warranty Start Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fecha de Inicio
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="date"
                  value={
                    value.warrantyStartDate
                      ? new Date(value.warrantyStartDate * 1000).toISOString().split('T')[0]
                      : ''
                  }
                  onChange={(e) => {
                    const date = e.target.value ? new Date(e.target.value).getTime() / 1000 : undefined;
                    handleChange('warrantyStartDate', date);
                  }}
                  disabled={disabled}
                  className={cn(
                    'w-full pl-10 pr-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500',
                    disabled && 'bg-gray-100 cursor-not-allowed'
                  )}
                />
              </div>
            </div>
          </div>

          {/* Pre-authorization Required */}
          <div className="mt-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={value.preAuthRequired || false}
                onChange={(e) => handleChange('preAuthRequired', e.target.checked)}
                disabled={disabled}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">
                Requiere preautorización del proveedor
              </span>
            </label>
          </div>
        </>
      )}
    </div>
  );
}
